import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { ItemLogUsage } from '@/types/itemLogUsage'
import { getTotalUsageDuration, getUsageDuration } from '@/utils/date'
import { useTranslation } from 'react-i18next'

const getPatientHn = (log: ItemLogUsage) => log.patientHn ?? log.patientHN ?? '-'
const getStartAt = (log: ItemLogUsage) => log.startAt ?? log.startDate
const getEndAt = (log: ItemLogUsage) => log.endAt ?? log.endDate

export function LogUsage({ usageLogs, useHeader }: { usageLogs: ItemLogUsage[]; useHeader?: boolean }) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const durationLabels = {
    hour: t('scans.usage.hourUnit'),
    minute: t('scans.usage.minuteUnit'),
  }

  if (!usageLogs || usageLogs.length === 0) return null

  return (
    <div data-testid="item-usage-history" className={useHeader ? 'mt-4 border-t border-border/30 pt-4' : ''}>
      {useHeader ? (
        <div className="mb-2 flex items-center justify-between text-xs font-medium text-foreground">
          <h3>
            {t('scans.usage.history')} ({usageLogs.length})
          </h3>
          <div className="text-success">{getTotalUsageDuration(usageLogs, durationLabels)}</div>
        </div>
      ) : (
        <div className="mb-4 flex justify-center p-1 text-sm text-success">
          {t('scans.usage.totalDuration', { duration: getTotalUsageDuration(usageLogs, durationLabels) })}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {usageLogs.slice(0, 5).map(log => {
          const startAt = getStartAt(log)
          const endAt = getEndAt(log)

          return (
            <div key={log.id} className="rounded border border-border/30 bg-secondary/30 p-2 text-xs">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-foreground">
                    {log.patientName || t('scans.usage.fallbackPatient')}
                  </p>
                  <p className="font-mono text-[10px] text-primary">HN {getPatientHn(log)}</p>
                </div>
                <span className="shrink-0 text-right text-[10px] text-muted-foreground">
                  {formatDateTime(startAt, {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {endAt
                    ? ` - ${formatDateTime(endAt, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`
                    : ''}
                </span>
              </div>

              <div className="mt-2 flex items-end justify-between gap-2">
                <div className="min-w-0 text-[11px] text-muted-foreground">
                  {log.departmentName ? <p className="truncate">{log.departmentName}</p> : null}
                  {log.note ? <p className="mt-0.5 truncate">{log.note}</p> : null}
                </div>
                <p className="shrink-0 text-[11px] text-success">{getUsageDuration(log, durationLabels)}</p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
